import './FileUpload.css';
import { useRef, useState } from 'react';
import { ClientOnlyFileModel, FileAttachmentStatus, FileData } from './FileUploadModels';
import { cx } from '#/Scripts/utilities/cx';
import { truncateFileNameForDisplay } from '#/Scripts/utilities/FilenameTools';
import { IconButton } from '#sign-up/components/common/Button/IconButton';
import Icon from '#sign-up/components/Icons/Icon';

type FileUploadProps = {
  fileList: FileData;
  setFileList: (fileList: FileData) => void;
  label?: string;
  acceptedFileTypes?: string;
  maxFileUploadCount: number;
  disabled?: boolean;
  maxSize?: number; // in bytes
  onRemoveFile?: (fileItem: ClientOnlyFileModel) => Promise<void>;
};

export const FileUpload = ({
  fileList,
  setFileList,
  label,
  acceptedFileTypes = '.jpeg,.jpg,.png,.pdf',
  maxFileUploadCount = 1,
  maxSize = 10 * 1024 * 1024, // 10MB default
  disabled,
  onRemoveFile,
}: FileUploadProps) => {
  const [dropZoneActive, setDropZoneActive] = useState<boolean>(false);
  const [fileError, setFileError] = useState('');
  const MAX_FILE_NAME_LENGTH = 100;
  const fileDropRoot = useRef<HTMLDivElement>(null);
  const canAddMoreFiles = fileList.size < maxFileUploadCount;

  const handleFileUpload = (files: FileList): void => {
    setFileError('');
    if (fileList.size + files.length > maxFileUploadCount) {
      setFileError(`Attempting to upload too many files. Maximum ${maxFileUploadCount} file(s) can be attached.`);
      return;
    }
    const updatedFileList = new Map(fileList);
    for (const file of files) {
      if (acceptedFileTypes) {
        const acceptedTypesArray = new Set(acceptedFileTypes.split(',').map((type) => type.trim().toLowerCase()));
        const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
        const isTypeAccepted = acceptedTypesArray.has(file.type.toLowerCase()) || acceptedTypesArray.has(fileExtension);
        if (!isTypeAccepted) {
          setFileError(`File type of "${file.name}" is not accepted.`);
          return;
        }
      }
      if (file.name.length > MAX_FILE_NAME_LENGTH) {
        setFileError(`File name must not exceed ${MAX_FILE_NAME_LENGTH} characters.`);
        return;
      }
      if (file.size > maxSize) {
        setFileError(`File "${file.name}" exceeds maximum size of ${(maxSize / (1024 * 1024)).toFixed(1)} MB`);
        return;
      }
      const newFileToUpload: ClientOnlyFileModel = {
        Name: file.name,
        FileSize: file.size,
        uid: crypto.randomUUID(),
        file,
        status: FileAttachmentStatus.pending,
        Type: file.type,
      };
      updatedFileList.set(newFileToUpload.uid, newFileToUpload);
      setFileList(updatedFileList);
    }
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { target } = event;
    if (target.files) {
      handleFileUpload(target.files);
    }
    target.value = '';
  };

  const removeFile = async (fileItem: ClientOnlyFileModel) => {
    setFileError('');
    if (onRemoveFile) {
      try {
        await onRemoveFile(fileItem);
        const finalFileList = new Map(fileList);
        finalFileList.delete(fileItem.uid);
        setFileList(finalFileList);
      } catch (error) {
        console.error('Failed to remove file:', error);
      }
    } else {
      const updatedFileList = new Map(fileList);
      updatedFileList.delete(fileItem.uid);
      setFileList(updatedFileList);
    }
  };

  const stopBaseEventAction = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const showDropZone = (event: React.DragEvent<HTMLDivElement>): void => {
    stopBaseEventAction(event);
    setDropZoneActive(true);
  };

  const hideDropZone = (event: React.DragEvent<HTMLDivElement>): void => {
    stopBaseEventAction(event);
    const leavingTheNode = event.target as HTMLElement;
    if (fileDropRoot.current === leavingTheNode || fileDropRoot?.current?.contains(leavingTheNode)) {
      setDropZoneActive(false);
    }
  };

  const filesDropped = (event: React.DragEvent<HTMLDivElement>): void => {
    stopBaseEventAction(event);
    setDropZoneActive(false);
    if (event.dataTransfer?.files) {
      handleFileUpload(event.dataTransfer?.files);
    }
  };

  const fileIcon = (type: string, name: string = '') => {
    const lowerType = type.toLowerCase();
    const lowerName = name.toLowerCase();
    if (lowerType.includes('pdf')) return 'picture_as_pdf';
    if (['jpeg', 'jpg', 'png'].some((ext) => lowerType.includes(ext))) return 'image';
    if (
      ['.xlsx', '.xls', '.csv'].some((ext) => lowerName.endsWith(ext)) ||
      ['spreadsheet', 'csv'].some((keyword) => lowerType.includes(keyword))
    )
      return 'grid_on';
    return 'description';
  };

  const formatFileSize = (size: number) => {
    if (size < 1024) return `${size} bytes`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div
      onDragEnter={showDropZone}
      onDragOver={showDropZone}
      onDragLeave={hideDropZone}
      onDrop={filesDropped}
      className={cx('file-upload-root')}
      data-testid="file-upload-root"
      ref={fileDropRoot}
    >
      {label && <label>{label}</label>}
      {canAddMoreFiles && (
        <label htmlFor="file-up" className="btn-flat icon text file-upload-button" aria-label="Upload Documents">
          <input
            accept={acceptedFileTypes}
            className="visually-hidden"
            disabled={disabled}
            id="file-up"
            multiple
            name="file-up"
            onChange={handleFileSelection}
            type="file"
          />
          {dropZoneActive ? (
            <Icon>progress_activity</Icon>
          ) : (
            <>
              <Icon>file_upload</Icon>
              <span className="upload-text">
                Browse File
              </span>
            </>
          )}
        </label>
      )}
      <div className="uploads">
        {[...fileList].map(([key, fileItem]) => {
          const originalName = fileItem.Name ?? '';

          return (
            <div className="file-block" key={key}>
              <div className="file-header">
                <Icon>{fileIcon(fileItem.Type, originalName)}</Icon>
                <div className="file-name">
                  <span title={originalName}>{truncateFileNameForDisplay(originalName)}</span>
                  {fileItem.FileSize && <span>{` (${formatFileSize(fileItem.FileSize)})`}</span>}
                </div>
                <IconButton
                  type="button"
                  iconName="close"
                  title="remove this document"
                  disabled={disabled}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    void removeFile(fileItem);
                  }}
                  aria-label={`Remove ${originalName}`}
                />
              </div>
            </div>
          );
        })}
      </div>
      {fileList.size === 0 && <p>Or drop it here to upload</p>}
      <p className="file-upload-message">
        Upload a file of type(s): {acceptedFileTypes.replaceAll(',', ', ')} (max {(maxSize / (1024 * 1024)).toFixed(1)}{' '}
        MB)
      </p>
      {fileError && (
        <p role="alert" className="error-text">
          {fileError}
        </p>
      )}
    </div>
  );
};
